import { SystemTranslationKeys } from '@common/entities';

import { FloorPlan } from 'types/floorplan-page-types';
import { formatCurrency, formatNumber } from 'utils/format';
import { formatAreaRange } from 'utils/formatting';

type TranslateSystem = (key: string, options?: Record<string, unknown>) => string;

export const formatFloorPlanCount = (value: number | null | undefined): string => {
  if (value == null) {
    return '--';
  }
  return formatNumber(value, { maximumFractionDigits: 0 });
};

export const getAmount = (minValue: number, maxValue?: number) => {
  // Return null if both values are null or 0
  if (
    (minValue === null || minValue === 0) &&
    (maxValue === null || maxValue === 0 || maxValue === undefined)
  ) {
    return null;
  }

  if (maxValue != null && minValue !== maxValue) {
    return `${formatCurrency(Number(minValue ?? 0))} - ${formatCurrency(Number(maxValue))}`;
  }

  return formatCurrency(Number(minValue ?? 0));
};

export const formatRentWithFrequency = (
  amount: string,
  rentFrequency: string | null | undefined,
  tSystem: TranslateSystem,
): string =>
  tSystem(SystemTranslationKeys.floorPlan.rentWithFrequency, {
    amount,
    frequency: rentFrequency || tSystem(SystemTranslationKeys.common.perMonth),
  });

export const getArea = (minSqFt: number, maxSqFt?: number) => {
  return formatAreaRange(minSqFt, maxSqFt);
};

export const getRemainingUnitText = (availableUnit: number, tSystem?: TranslateSystem) => {
  const formattedCount = formatNumber(availableUnit, { maximumFractionDigits: 0 });

  if (!tSystem) return `${formattedCount} Units Available`;
  if (availableUnit === 0) {
    return tSystem('floorPlan.noUnitsAvailableText');
  }
  if (availableUnit === 1) {
    return tSystem('floorPlan.onlyUnitAvailable', { count: formattedCount });
  }
  return tSystem('floorPlan.unitsAvailableText', { count: formattedCount });
};

export const getAvailabilityTextColor = (availableUnit: number): string => {
  if (availableUnit <= 0) return 'gray.500';
  if (availableUnit === 1) return 'orange.700';
  return 'green.dark';
};

/**
 * Returns the appropriate available units count based on user context
 * @param floorPlan - The floor plan object
 * @param isLeadProfile - Whether the context of the request is lead profile or not
 * @returns The available units count to display
 */
export const getAvailableUnitCountForContext = (floorPlan: FloorPlan, isLeadProfile: boolean) => {
  if (isLeadProfile) {
    return floorPlan.availableUnits;
  }
  return floorPlan.availableUnitsWebVisible;
};
